// src/pages/UserMeet/UserMeetPage.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Video,
  Users,
  Search,
  RefreshCw,
  Clock,
  User,
  Copy,
  Check,
  XCircle,
  Mail,
  CalendarClock,
} from "lucide-react";
import {
  getAllMeetings,
  getMyMeetings,
  endMeeting,
} from "@/Services/UserMeetServices";

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Determine meeting lifecycle state: "upcoming" | "active" | "ended" */
function getMeetingState(meeting) {
  const now = Date.now();
  const isActive = meeting.isActive !== false;
  const scheduledAt = meeting.scheduledAt
    ? new Date(meeting.scheduledAt).getTime()
    : null;
  const scheduledEndAt = meeting.scheduledEndAt
    ? new Date(meeting.scheduledEndAt).getTime()
    : null;

  if (!isActive) return "ended";
  if (scheduledAt && now < scheduledAt) return "upcoming";
  if (scheduledEndAt && now > scheduledEndAt) return "ended";
  return "active";
}

/** Format an ISO datetime string for display. */
function formatScheduledTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Small countdown formatter — "2d 3h", "5h 20m", "12m" */
function formatCountdown(ms) {
  if (ms <= 0) return "now";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export default function UserMeetPage() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("all"); // "all" | "my"
  const [meetings, setMeetings] = useState([]);
  const [myMeetings, setMyMeetings] = useState({
    created: [],
    participated: [],
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    meetingId: null,
  });

  // Fetch all meetings
  const fetchAllMeetings = async () => {
    try {
      setLoading(true);
      const res = await getAllMeetings();

      if (res?.success) {
        setMeetings(res.data || []);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load meetings");
    } finally {
      setLoading(false);
    }
  };

  // Fetch my meetings
  const fetchMyMeetings = async () => {
    try {
      setLoading(true);
      const res = await getMyMeetings();

      if (res?.success) {
        setMyMeetings(res.data || { created: [], participated: [] });
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load your meetings");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (activeTab === "all") {
      fetchAllMeetings();
    } else {
      fetchMyMeetings();
    }
  }, [activeTab]);

  // Filter meetings by search
  const filteredMeetings = meetings.filter(
    (m) =>
      m.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.creator?.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredCreated = myMeetings.created.filter((m) =>
    m.title?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredParticipated = myMeetings.participated.filter((m) =>
    m.title?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const openEndMeetingModal = (meetingId) => {
    setConfirmModal({
      open: true,
      meetingId,
    });
  };

  const confirmEndMeeting = async () => {
    try {
      await endMeeting(confirmModal.meetingId);
      toast.success("Meeting ended successfully");

      setConfirmModal({
        open: false,
        meetingId: null,
      });

      if (activeTab === "all") {
        fetchAllMeetings();
      } else {
        fetchMyMeetings();
      }
    } catch (err) {
      toast.error(err?.message || "Failed to end meeting");
    }
  };

  return (
    <div className="min-h-screen pb-20">
      <div className="container-x py-12">
        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
          <div>
            <p className="overline mb-2">Video Meetings</p>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight">
              User<span className="italic text-[#C6A87C]">Meet</span>
            </h1>
            <p className="text-white/50 text-sm mt-2">
              Join or create instant video meetings (50-person max)
            </p>
          </div>

          <button
            onClick={() => navigate("/user-meet/create")}
            className="btn-gold justify-center md:w-auto"
          >
            <Plus size={18} />
            Create Meeting
          </button>
        </div>

        {/* ── Tabs ── */}
        <div className="flex items-center gap-4 mb-8 border-b border-white/10">
          <button
            onClick={() => setActiveTab("all")}
            className={`pb-3 px-2 text-sm font-medium transition-colors border-b-2 ${
              activeTab === "all"
                ? "text-[#C6A87C] border-[#C6A87C]"
                : "text-white/50 border-transparent hover:text-white/70"
            }`}
          >
            All Meetings
          </button>
          <button
            onClick={() => setActiveTab("my")}
            className={`pb-3 px-2 text-sm font-medium transition-colors border-b-2 ${
              activeTab === "my"
                ? "text-[#C6A87C] border-[#C6A87C]"
                : "text-white/50 border-transparent hover:text-white/70"
            }`}
          >
            My Meetings
          </button>
        </div>

        {/* ── Search & Refresh ── */}
        <div className="flex items-center gap-3 mb-8">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search meetings..."
              className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg pl-11 pr-4 py-3
                         focus:border-[#C6A87C] focus:outline-none transition-colors
                         text-white placeholder:text-white/30 text-sm"
            />
          </div>

          <button
            onClick={activeTab === "all" ? fetchAllMeetings : fetchMyMeetings}
            disabled={loading}
            className="glass border border-white/10 rounded-lg p-3 hover:border-[#C6A87C]/50 
                       transition-colors disabled:opacity-40"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <LoadingState />
        ) : activeTab === "all" ? (
          <AllMeetingsGrid
            meetings={filteredMeetings}
            onEndMeeting={openEndMeetingModal}
          />
        ) : (
          <MyMeetingsGrid
            created={filteredCreated}
            participated={filteredParticipated}
            onEndMeeting={openEndMeetingModal}
          />
        )}
      </div>
      <ConfirmEndMeetingModal
        open={confirmModal.open}
        loading={loading}
        onClose={() =>
          setConfirmModal({
            open: false,
            meetingId: null,
          })
        }
        onConfirm={confirmEndMeeting}
      />
    </div>
  );
}

// ==================== SUB-COMPONENTS ====================

function LoadingState() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="glass rounded-2xl p-6 animate-pulse">
          <div className="h-6 bg-white/10 rounded mb-4 w-3/4" />
          <div className="h-4 bg-white/5 rounded mb-2 w-full" />
          <div className="h-4 bg-white/5 rounded w-2/3" />
        </div>
      ))}
    </div>
  );
}

function AllMeetingsGrid({ meetings, onEndMeeting }) {
  const navigate = useNavigate();

  // ✅ Get current user ID to determine if they're the creator
  const myUserId = localStorage.getItem("userId");

  if (meetings.length === 0) {
    return (
      <div className="glass rounded-2xl p-12 text-center">
        <Video size={48} className="mx-auto text-white/20 mb-4" />
        <p className="text-white/40">No active meetings found</p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {meetings.map((meeting) => {
        // ✅ Check if current user is the creator
        const isCreator =
          myUserId && meeting.creator?._id
            ? String(myUserId) === String(meeting.creator._id)
            : false;

        return (
          <MeetingCard
            key={meeting.meetingId || meeting._id}
            meeting={meeting}
            isCreator={isCreator} // ✅ Pass isCreator prop
            onClick={() => navigate(`/user-meet/join/${meeting.joinCode}`)}
            onEndMeeting={onEndMeeting}
          />
        );
      })}
    </div>
  );
}

function MyMeetingsGrid({ created, participated, onEndMeeting }) {
  const navigate = useNavigate();

  // ── Split created into upcoming vs current (active/full/ended) ──
  const upcomingCreated = created.filter(
    (m) => getMeetingState(m) === "upcoming",
  );
  const currentCreated = created.filter(
    (m) => getMeetingState(m) !== "upcoming",
  );

  return (
    <div className="space-y-12">
      {/* Upcoming (scheduled) — only shown if there are any */}
      {upcomingCreated.length > 0 && (
        <div>
          <h3 className="overline mb-4">
            Upcoming ({upcomingCreated.length})
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcomingCreated.map((meeting) => (
              <MeetingCard
                key={meeting.meetingId || meeting._id}
                meeting={meeting}
                isCreator
                onClick={() => navigate(`/user-meet/join/${meeting.joinCode}`)}
                onEndMeeting={onEndMeeting}
              />
            ))}
          </div>
        </div>
      )}

      {/* Created by me */}
      <div>
        <h3 className="overline mb-4">
          Created by Me ({currentCreated.length})
        </h3>
        {currentCreated.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center">
            <p className="text-white/40 text-sm">
              You haven't created any meetings yet
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentCreated.map((meeting) => (
              <MeetingCard
                key={meeting.meetingId || meeting._id}
                meeting={meeting}
                isCreator
                onClick={() => navigate(`/user-meet/join/${meeting.joinCode}`)}
                onEndMeeting={onEndMeeting}
              />
            ))}
          </div>
        )}
      </div>

      {/* Participated */}
      <div>
        <h3 className="overline mb-4">Participated ({participated.length})</h3>
        {participated.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center">
            <p className="text-white/40 text-sm">
              You haven't joined any meetings yet
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {participated.map((meeting) => (
              <MeetingCard
                key={meeting.meetingId || meeting._id}
                meeting={meeting}
                onClick={() => navigate(`/user-meet/join/${meeting.joinCode}`)}
                onEndMeeting={onEndMeeting}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfirmEndMeetingModal({ open, onClose, onConfirm, loading }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass w-full max-w-md rounded-2xl border border-white/10 p-8 shadow-2xl"
      >
        {/* Header */}
        <p className="overline mb-2">Confirmation</p>

        <h2 className="font-display text-3xl tracking-tight mb-3">
          End
          <span className="italic text-[#C6A87C]"> Meeting?</span>
        </h2>

        <p className="text-white/50 leading-relaxed text-sm">
          This will immediately end the meeting for everyone currently
          connected. Participants will be disconnected and won't be able to
          rejoin this session.
        </p>

        {/* Buttons */}
        <div className="mt-8 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2.5 rounded-lg border border-white/10 text-white/70
                       hover:border-white/20 hover:bg-white/5 transition-all
                       disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-5 py-2.5 rounded-lg
             bg-[#C6A87C]/15
             border border-[#C6A87C]/40
             text-[#C6A87C]
             hover:bg-[#C6A87C]/25
             hover:border-[#C6A87C]/60
             transition-all duration-200
             disabled:opacity-50
             disabled:cursor-not-allowed"
          >
            {loading ? "Ending..." : "End Meeting"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MeetingCard({ meeting, isCreator, onClick, onEndMeeting }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // ── Lifecycle state ──────────────────────────────────────────────────────────
  const state = getMeetingState(meeting); // "upcoming" | "active" | "ended"
  const isFull = meeting.currentOccupancy >= (meeting.capacity || 8);
  const isUpcoming = state === "upcoming";
  const isActive = state === "active";
  const isEnded = state === "ended";
  const meetingId = meeting.meetingId || meeting._id;

  // Countdown for upcoming meetings
  const startsInMs = meeting.scheduledAt
    ? new Date(meeting.scheduledAt).getTime() - Date.now()
    : null;

  // Only active + not full meetings are joinable
  const canJoin = isActive && !isFull;

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(meeting.joinCode);
    setCopied(true);
    toast.success("Join code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEnd = (e) => {
    e.stopPropagation();
    onEndMeeting(meetingId);
  };

  // ── Status pill config ───────────────────────────────────────────────────────
  const pillKey = isActive && isFull ? "full" : state;
  const statusPill = {
    upcoming: { text: "Upcoming", cls: "bg-blue-500/20 text-blue-300" },
    active: { text: "Active", cls: "bg-green-500/20 text-green-300" },
    full: { text: "Full", cls: "bg-orange-500/20 text-orange-300" },
    ended: { text: "Ended", cls: "bg-red-500/20 text-red-300" },
  }[pillKey];

  return (
    <div
      onClick={canJoin ? onClick : undefined}
      className={`glass rounded-2xl p-6 transition-all ${
        canJoin
          ? "cursor-pointer hover:border-[#C6A87C]/50 hover:scale-[1.02]"
          : isUpcoming
            ? "cursor-default border-blue-500/20"
            : "opacity-60 cursor-not-allowed"
      }`}
    >
      {/* Status badge */}
      <div className="flex items-center justify-between mb-4">
        <span className={`text-xs px-2 py-1 rounded-full ${statusPill.cls}`}>
          {statusPill.text}
        </span>

        {isCreator && <span className="text-xs text-[#C6A87C]">Creator</span>}
      </div>

      {/* Title */}
      <h3 className="font-display text-xl tracking-tight mb-2 truncate">
        {meeting.title || "Untitled Meeting"}
      </h3>

      {/* Description */}
      {meeting.description && (
        <p className="text-white/50 text-sm line-clamp-2 mb-4">
          {meeting.description}
        </p>
      )}

      {/* Scheduled banner — only for upcoming meetings */}
      {isUpcoming && meeting.scheduledAt && (
        <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <div className="flex items-center gap-2 text-blue-300 text-xs mb-1">
            <CalendarClock size={12} />
            <span className="font-medium">Scheduled</span>
          </div>
          <p className="text-white/70 text-sm">
            {formatScheduledTime(meeting.scheduledAt)}
          </p>
          {startsInMs > 0 && (
            <p className="text-white/40 text-xs mt-1">
              Starts in {formatCountdown(startsInMs)}
            </p>
          )}
        </div>
      )}

      {/* Meta info */}
      <div className="space-y-2 text-xs">
        <div className="flex items-center gap-2 text-white/40">
          <User size={12} />
          <span>{meeting.creator?.name || "Unknown"}</span>
        </div>

        <div className="flex items-center gap-2 text-white/40">
          <Users size={12} />
          <span>
            {meeting.currentOccupancy || 0}/{meeting.capacity || 8} participants
          </span>
        </div>

        <div className="flex items-center gap-2 text-white/40">
          <Clock size={12} />
          <span>
            {new Date(meeting.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      </div>

      {/* ✅ Join code section with copy and end buttons */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center justify-between gap-3">
          {/* Left: Join code label + code + copy button */}
          <div className="flex-1">
            <p className="text-xs text-white/30 mb-1">Join Code</p>
            <div className="flex items-center gap-2">
              <p className="text-[#C6A87C] font-mono text-sm">
                {meeting.joinCode}
              </p>

              <button
                onClick={handleCopy}
                className="p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
                title="Copy join code"
              >
                {copied ? (
                  <Check size={14} className="text-green-400" />
                ) : (
                  <Copy size={14} className="text-white/50" />
                )}
              </button>
            </div>
          </div>

          {/* Right: Creator actions (Invite + End) — hidden for ended meetings */}
          {isCreator && !isEnded && (
            <div className="flex items-center gap-2">
              {/* Invite button — available for both upcoming & active */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/user-meet/${meetingId}/invite`, {
                    state: { meetingData: meeting },
                  });
                }}
                className="px-3 py-2 rounded-lg
         bg-blue-500/15
         hover:bg-blue-500/25
         border border-blue-500/40
         hover:border-blue-500/60
         transition-colors duration-200
         text-xs font-medium text-blue-400
         flex items-center gap-1.5"
                title="Send calendar invites"
              >
                <Mail size={14} />
                Invite
              </button>

              {/* End button — only for active meetings (not upcoming) */}
              {isActive && (
                <button
                  onClick={handleEnd}
                  className="px-3 py-2 rounded-lg
         bg-[#C6A87C]/15
         hover:bg-[#C6A87C]/25
         border border-[#C6A87C]/40
         hover:border-[#C6A87C]/60
         transition-colors duration-200
         disabled:opacity-50
         disabled:cursor-not-allowed
         text-xs font-medium text-[#C6A87C]
         flex items-center gap-1.5"
                  title="End meeting for everyone"
                >
                  <XCircle size={14} />
                  End
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}